import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { PartnerRegistrationFormData } from '~/types/partnerRegistration'

const STORAGE_KEY = 'partner-registration-form-data'

const loadFromLocalStorage = (): Partial<PartnerRegistrationFormData> => {
  if (!import.meta.client) {
    return {}
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error(
      'Error loading partner registration data from localStorage',
      error
    )
  }

  return {}
}

const saveToLocalStorage = (data: Partial<PartnerRegistrationFormData>) => {
  if (!import.meta.client) {
    return
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error(
      'Error saving partner registration data to localStorage',
      error
    )
  }
}

const clearLocalStorage = () => {
  if (!import.meta.client) {
    return
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error(
      'Error clearing partner registration data from localStorage',
      error
    )
  }
}

export const usePartnerRegistrationStore = defineStore(
  'partnerRegistration',
  () => {
    const formData = ref<Partial<PartnerRegistrationFormData>>(
      loadFromLocalStorage()
    )

    let saveTimeout: NodeJS.Timeout | null = null

    // Debounced save to localStorage - only save after 300ms of no changes
    const debouncedSave = (data: Partial<PartnerRegistrationFormData>) => {
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
      saveTimeout = setTimeout(() => {
        saveToLocalStorage(data)
        saveTimeout = null
      }, 300)
    }

    const updateData = (data: Partial<PartnerRegistrationFormData>) => {
      formData.value = { ...formData.value, ...data }
      // Save to localStorage when data is explicitly updated
      debouncedSave(formData.value)
    }

    const clearFormData = () => {
      formData.value = {}
      if (saveTimeout) {
        clearTimeout(saveTimeout)
        saveTimeout = null
      }
      clearLocalStorage()
    }

    return {
      formData: computed(() => formData.value),
      updateData,
      clearFormData,
    }
  }
)
